/**
 * ProIcon Component
 * 
 * Universal Font Awesome Pro+ icon wrapper for SUNMARBELLA.
 * Replaces FontAwesomeIcon and DuotoneIcon with a single asset-based component.
 * 
 * Features:
 * - 7 launch families: light, solid, brands, utility, sharp, graphite, duotone
 * - Desktop/mobile family override support (for Graphite search treatment)
 * - Duotone color support
 * - Accessibility support
 * - Responsive sizing
 * 
 * @version 1.0.0
 */

import React from 'react';

export type IconFamily = 
  | 'light'      // Classic Light (default)
  | 'solid'      // Classic Solid (filled states)
  | 'brands'     // Classic Brands
  | 'utility'    // Utility Fill Semibold (controls)
  | 'sharp'      // Sharp Regular (metadata/tags)
  | 'graphite'   // Graphite Thin (desktop search accent)
  | 'duotone'    // Duotone Light (reviews)
  | 'classic-duotone'; // Classic Duotone Solid

export type IconSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl';

export interface ProIconProps {
  /** Icon name (kebab-case, e.g., 'magnifying-glass', 'chevron-right') */
  icon: string;
  
  /** Icon family/style */
  family?: IconFamily;
  
  /** 
   * Optional desktop-only family override.
   * Use this for Graphite desktop search treatment.
   * When provided, this family is used on desktop (>=768px),
   * and the main `family` prop is used on mobile.
   */
  desktopFamily?: IconFamily;
  
  /** Icon size */
  size?: IconSize;
  
  /** Additional CSS classes */
  className?: string;
  
  /** 
   * Whether this icon is decorative only.
   * If true, aria-hidden is applied.
   * If false, provide ariaLabel for accessibility.
   */
  decorative?: boolean;
  
  /** Accessible label (required when decorative=false) */
  ariaLabel?: string;
  
  /** Title for tooltip */
  title?: string;
  
  /** Fixed width (aligns icons in lists) */
  fixedWidth?: boolean;
  
  /** 
   * Duotone primary color (CSS color value).
   * Only applies when family='duotone'.
   */
  primaryColor?: string;
  
  /**
   * Duotone secondary color (CSS color value).
   * Only applies when family='duotone'.
   */
  secondaryColor?: string;
  
  /**
   * Duotone secondary opacity (0-1).
   * Default: 0.4
   * Only applies when family='duotone'.
   */
  secondaryOpacity?: number;
  
  /** Click handler */
  onClick?: () => void;
}

/**
 * Family to CSS class mapping
 */
const FAMILY_CLASSES: Record<IconFamily, string> = {
  light: 'fal',
  solid: 'fas',
  brands: 'fab',
  utility: 'faufsb',
  sharp: 'fasr',
  graphite: 'fagt',
  duotone: 'fadl',
  'classic-duotone': 'fad',
};

/**
 * Size to CSS class mapping
 */
const SIZE_CLASSES: Record<IconSize, string> = {
  xs: 'fa-icon-xs',
  sm: 'fa-icon-sm',
  base: '',
  lg: 'fa-icon-lg',
  xl: 'fa-icon-xl',
  '2xl': 'fa-icon-2xl',
};

/**
 * Detect if current viewport is mobile
 */
const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
};

/**
 * ProIcon - Universal Font Awesome Pro+ icon component
 */
export const ProIcon: React.FC<ProIconProps> = ({
  icon,
  family = 'light',
  desktopFamily,
  size = 'base',
  className = '',
  decorative = false,
  ariaLabel,
  title,
  fixedWidth = false,
  primaryColor,
  secondaryColor,
  secondaryOpacity = 0.4,
  onClick,
}) => {
  // Determine effective family (handle desktop override)
  const effectiveFamily = React.useMemo<IconFamily>(() => {
    if (desktopFamily && !isMobile()) {
      return desktopFamily;
    }
    return family;
  }, [family, desktopFamily]);

  // Build CSS classes
  const familyClass = FAMILY_CLASSES[effectiveFamily];
  const sizeClass = SIZE_CLASSES[size];
  const fixedWidthClass = fixedWidth ? 'fa-fw' : '';
  
  const classes = [
    familyClass,
    sizeClass,
    fixedWidthClass,
    `fa-${icon}`,
    className,
  ].filter(Boolean).join(' ');

  // Build duotone styles
  const duotoneStyles: React.CSSProperties = effectiveFamily === 'duotone' 
    ? {
        '--fa-primary-color': primaryColor || 'currentColor',
        '--fa-secondary-color': secondaryColor || 'currentColor',
        '--fa-secondary-opacity': secondaryOpacity,
      } as React.CSSProperties
    : {};

  // Build aria attributes
  const ariaAttributes: React.AriaAttributes = decorative
    ? { 'aria-hidden': true }
    : { 'aria-label': ariaLabel || icon };

  return (
    <i
      className={classes}
      style={duotoneStyles}
      title={title}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      {...ariaAttributes}
    />
  );
};

/**
 * Hook to detect mobile viewport for responsive family switching
 * 
 * Usage:
 * const isMobileViewport = useIsMobile();
 * const family = isMobileViewport ? 'utility' : 'graphite';
 */
export const useIsMobile = (): boolean => {
  const [isMobileViewport, setIsMobileViewport] = React.useState<boolean>(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };

    // Check on mount
    checkMobile();

    // Listen for resize
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobileViewport;
};

/**
 * Preset color schemes for duotone icons
 * These match the design token patterns
 */
export const DUOTONE_SCHEMES = {
  /** Brand ocean blue tones */
  ocean: {
    primaryColor: '#0F4C81',
    secondaryColor: '#4A90A4',
    secondaryOpacity: 0.3,
  },
  
  /** Brand sun orange tones */
  sun: {
    primaryColor: '#F5A623',
    secondaryColor: '#FBD38D',
    secondaryOpacity: 0.4,
  },
  
  /** Success green tones */
  success: {
    primaryColor: '#48BB78',
    secondaryColor: '#9AE6B4',
    secondaryOpacity: 0.4,
  },
  
  /** Slate tones for subtle emphasis */
  slate: {
    primaryColor: '#64748B',
    secondaryColor: '#94A3B8',
    secondaryOpacity: 0.4,
  },
  
  /** White tones for dark backgrounds */
  white: {
    primaryColor: '#FFFFFF',
    secondaryColor: '#E2E8F0',
    secondaryOpacity: 0.4,
  },
} as const;

export default ProIcon;
